import axios, { AxiosResponse } from 'axios';
import { User, List, Item, CreateListData, CreateItemData, UpdateItemData, Group, CreateGroupData, UserGroup, ApiResponse, UserSettings, UpdateSettingsData, ListTemplate } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Добавляем токен к каждому запросу
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Обрабатываем ответы
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      // Не делаем принудительное перенаправление, пусть компоненты сами решают
      // window.location.href = '/login';
    }
    
    // Улучшаем обработку сетевых ошибок
    if (!error.response) {
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        error.message = 'Превышено время ожидания ответа от сервера';
      } else if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
        error.message = 'Ошибка сети. Проверьте подключение к интернету';
      } else if (error.message?.includes('ERR_NETWORK')) {
        error.message = 'Не удается подключиться к серверу';
      }
    }
    
    return Promise.reject(error);
  }
);

// Auth API helpers
export const passwordApi = {
  forgot: async (email: string): Promise<void> => {
    await api.post('/auth/forgot-password', { email });
  },
  reset: async (token: string, password: string): Promise<void> => {
    await api.post('/auth/reset-password', { token, password });
  },
};

// Auth API
export const authApi = {
  login: async (email: string, password: string): Promise<{ user: User; token: string }> => {
    const response: AxiosResponse<ApiResponse<{ user: User; token: string }>> = await api.post('/auth/login', {
      email,
      password,
    });
    return response.data.data!;
  },

  register: async (username: string, email: string, password: string): Promise<{ user: User; token: string }> => {
    const response: AxiosResponse<ApiResponse<{ user: User; token: string }>> = await api.post('/auth/register', {
      name: username,
      email,
      password,
    });
    return response.data.data!;
  },

  getProfile: async (): Promise<User> => {
    const response: AxiosResponse<ApiResponse<User>> = await api.get('/auth/profile');
    return response.data.data!;
  },
};

// Lists API
export const listsApi = {
  getAll: async (): Promise<List[]> => {
    const response: AxiosResponse<ApiResponse<List[]>> = await api.get('/lists');
    return response.data.data!;
  },

  getById: async (id: string): Promise<List> => {
    const response: AxiosResponse<ApiResponse<List>> = await api.get(`/lists/${id}`);
    return response.data.data!;
  },

  create: async (data: CreateListData): Promise<List> => {
    const response: AxiosResponse<ApiResponse<List>> = await api.post('/lists', data);
    return response.data.data!;
  },

  update: async (id: string, data: Partial<CreateListData>): Promise<List> => {
    const response: AxiosResponse<ApiResponse<List>> = await api.put(`/lists/${id}`, data);
    return response.data.data!;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/lists/${id}`);
  },

  getPublic: async (): Promise<List[]> => {
    const response: AxiosResponse<ApiResponse<List[]>> = await api.get('/lists/public');
    return response.data.data!;
  },

  getPublicById: async (id: string): Promise<List> => {
    const response: AxiosResponse<ApiResponse<List>> = await api.get(`/lists/public/${id}`);
    return response.data.data!;
  },

  // Публичное редактирование параметров списка отключено
  // Можно редактировать только элементы списка через itemsApi.createPublic, updatePublic, deletePublic

  getPublicLink: async (id: string): Promise<{ link: string; listId: string; listTitle: string; isPublic: boolean; publicEditable: boolean }> => {
    const response: AxiosResponse<ApiResponse<{ link: string; listId: string; listTitle: string; isPublic: boolean; publicEditable: boolean }>> = await api.get(`/lists/${id}/public-link`);
    return response.data.data!;
  },

  archive: async (id: string): Promise<List> => {
    const response: AxiosResponse<ApiResponse<List>> = await api.post(`/lists/${id}/archive`);
    return response.data.data!;
  },

  unarchive: async (id: string): Promise<List> => {
    const response: AxiosResponse<ApiResponse<List>> = await api.post(`/lists/${id}/unarchive`);
    return response.data.data!;
  },

  getArchived: async (): Promise<List[]> => {
    const response: AxiosResponse<ApiResponse<List[]>> = await api.get('/lists/archived');
    return response.data.data!;
  },

  exportMarkdown: async (id: string): Promise<{ filename: string; content: string }> => {
    const response: AxiosResponse<ApiResponse<{ filename: string; content: string }>> = await api.get(`/lists/${id}/export`);
    return response.data.data!;
  },

  createFromTemplate: async (templateId: string, overrides?: Partial<CreateListData>): Promise<List> => {
    const response: AxiosResponse<ApiResponse<List>> = await api.post(`/lists/templates/${templateId}`, overrides || {});
    return response.data.data!;
  },

  listTemplates: async (): Promise<ListTemplate[]> => {
    const response: AxiosResponse<ApiResponse<ListTemplate[]>> = await api.get('/lists/templates');
    return response.data.data!;
  },
};

// Items API
export const itemsApi = {
  getByListId: async (listId: string): Promise<Item[]> => {
    const response: AxiosResponse<ApiResponse<Item[]>> = await api.get(`/items/list/${listId}`);
    return response.data.data!;
  },

  create: async (data: CreateItemData): Promise<Item> => {
    const response: AxiosResponse<ApiResponse<Item>> = await api.post('/items', data);
    return response.data.data!;
  },

  update: async (id: string, data: UpdateItemData): Promise<Item> => {
    const response: AxiosResponse<ApiResponse<Item>> = await api.put(`/items/${id}`, data);
    return response.data.data!;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/items/${id}`);
  },

  complete: async (id: string, completionResult?: string): Promise<Item> => {
    const response: AxiosResponse<ApiResponse<Item>> = await api.patch(`/items/${id}/complete`, {
      completionResult,
    });
    return response.data.data!;
  },

  reorder: async (listId: string, orderedIds: string[]): Promise<void> => {
    await api.put(`/items/lists/${listId}/reorder`, { orderedIds });
  },

  // Публичные методы (без авторизации)
  getPublicByListId: async (listId: string): Promise<Item[]> => {
    const response: AxiosResponse<ApiResponse<Item[]>> = await api.get(`/items/public/lists/${listId}/items`);
    return response.data.data!;
  },

  createPublic: async (data: CreateItemData): Promise<Item> => {
    const response: AxiosResponse<ApiResponse<Item>> = await api.post('/items/public', data);
    return response.data.data!;
  },

  updatePublic: async (id: string, data: UpdateItemData): Promise<Item> => {
    const response: AxiosResponse<ApiResponse<Item>> = await api.put(`/items/public/${id}`, data);
    return response.data.data!;
  },

  deletePublic: async (id: string): Promise<void> => {
    await api.delete(`/items/public/${id}`);
  },
};

// Groups API
export const groupsApi = {
  getAll: async (): Promise<Group[]> => {
    const response: AxiosResponse<ApiResponse<Group[]>> = await api.get('/groups');
    return response.data.data!;
  },

  getMyGroups: async (): Promise<Group[]> => {
    const response: AxiosResponse<ApiResponse<Group[]>> = await api.get('/groups/my-groups');
    return response.data.data!;
  },

  getById: async (id: string): Promise<Group> => {
    const response: AxiosResponse<ApiResponse<Group>> = await api.get(`/groups/${id}`);
    return response.data.data!;
  },

  create: async (data: CreateGroupData): Promise<Group> => {
    const response: AxiosResponse<ApiResponse<Group>> = await api.post('/groups', data);
    return response.data.data!;
  },

  update: async (id: string, data: Partial<CreateGroupData>): Promise<Group> => {
    const response: AxiosResponse<ApiResponse<Group>> = await api.put(`/groups/${id}`, data);
    return response.data.data!;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/groups/${id}`);
  },

  join: async (id: string): Promise<UserGroup> => {
    const response: AxiosResponse<ApiResponse<UserGroup>> = await api.post(`/groups/${id}/join`);
    return response.data.data!;
  },

      leave: async (id: string): Promise<void> => {
        await api.post(`/groups/${id}/leave`);
      },

      invite: async (id: string, email: string): Promise<{ user: User; status: string }> => {
        const response: AxiosResponse<ApiResponse<{ user: User; status: string }>> = await api.post(`/groups/${id}/invite`, { email });
        return response.data.data!;
      },
    };

// Settings API
export const settingsApi = {
  get: async (): Promise<UserSettings> => {
    const response: AxiosResponse<ApiResponse<UserSettings>> = await api.get('/settings');
    return response.data.data!;
  },

  update: async (data: UpdateSettingsData): Promise<UserSettings> => {
    const response: AxiosResponse<ApiResponse<UserSettings>> = await api.put('/settings', data);
    return response.data.data!;
  },
};

// Profile API
export const profileApi = {
  update: async (data: { username?: string }): Promise<User> => {
    const response: AxiosResponse<ApiResponse<User>> = await api.put('/profile', data);
    return response.data.data!;
  },
};

// Statistics API
export const statisticsApi = {
  get: async (): Promise<{
    listsCount: number;
    completedItemsCount: number;
    groupsCount: number;
  }> => {
    const response: AxiosResponse<ApiResponse<{
      listsCount: number;
      completedItemsCount: number;
      groupsCount: number;
    }>> = await api.get('/statistics');
    return response.data.data!;
  },
};

export default api;